import React from 'react';
import { FiSearch, FiRefreshCw, FiEye, FiEdit, FiFileText, FiFile, FiX } from 'react-icons/fi';
import UserStatusBadge from './UserStatusBadge';

/**
 * Table of users with search, export, pagination, and row actions (view / edit).
 * Row click opens user details (view). Search clear resets search and refetches.
 */
const UserTable = ({
  users = [],
  loading,
  pagination,
  search,
  onSearchChange,
  onSearchSubmit,
  onClearSearch,
  onRefresh,
  onExportPDF,
  onExportExcel,
  exportLoading,
  onViewUser,
  onEditUser,
}) => {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <form className="relative flex items-center" onSubmit={onSearchSubmit}>
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="text"
            className="pl-10 pr-20 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 w-full md:w-64"
            placeholder="Search users..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          <div className="absolute right-1 top-1/2 transform -translate-y-1/2 flex items-center gap-0.5">
            {search.trim() && onClearSearch ? (
              <button
                type="button"
                onClick={onClearSearch}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                title="Clear search"
              >
                <FiX className="w-4 h-4" />
              </button>
            ) : null}
            <button
              type="submit"
              className="px-2 py-1 text-orange-500 hover:text-orange-600 text-sm font-medium"
            >
              Search
            </button>
          </div>
        </form>
        <button
          type="button"
          onClick={onRefresh}
          className="p-2 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200"
          title="Refresh"
        >
          <FiRefreshCw />
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onExportPDF}
            disabled={exportLoading}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Download as PDF"
          >
            <FiFileText className="w-4 h-4" />
            {exportLoading ? 'Exporting...' : 'PDF'}
          </button>
          <button
            type="button"
            onClick={onExportExcel}
            disabled={exportLoading}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Download as Excel"
          >
            <FiFile className="w-4 h-4" />
            {exportLoading ? 'Exporting...' : 'Excel'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Username
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Joined
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Profile
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
                    </div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => {
                  const avatarUrl = user.profileImage?.url || user.instagramProfilePicture;
                  return (
                  <tr
                    key={user._id}
                    onClick={() => onViewUser(user)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {avatarUrl ? (
                          <img
                            src={avatarUrl}
                            alt={user.username || 'User'}
                            className="h-8 w-8 rounded-full object-cover mr-3"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div
                          className={`h-8 w-8 rounded-full bg-orange-500 flex items-center justify-center text-white font-medium mr-3 ${
                            avatarUrl ? 'hidden' : ''
                          }`}
                        >
                          {user.username?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{user.username}</div>
                          <div className="text-xs text-gray-500">{user.instagramId}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.email || 'No email'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <UserStatusBadge status={user.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.isProfileComplete
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {user.isProfileComplete ? 'Complete' : 'Incomplete'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 space-x-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => onViewUser(user)}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                        title="View Details"
                      >
                        <FiEye className="w-5 h-5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onEditUser(user)}
                        className="text-green-600 hover:text-green-900 ml-2 p-1 rounded hover:bg-green-50"
                        title="Edit User"
                      >
                        <FiEdit className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.pages > 1 && (
          <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200">
            <p className="text-sm text-gray-700">
              Showing{' '}
              <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
              <span className="font-medium">
                {Math.min(pagination.page * pagination.limit, pagination.total)}
              </span>{' '}
              of <span className="font-medium">{pagination.total}</span> users
            </p>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={pagination.onPrevious}
                disabled={pagination.page <= 1}
                className={`px-4 py-2 text-sm rounded-md ${
                  pagination.page <= 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                }`}
              >
                Previous
              </button>
              <button
                type="button"
                onClick={pagination.onNext}
                disabled={pagination.page >= pagination.pages}
                className={`px-4 py-2 text-sm rounded-md ${
                  pagination.page >= pagination.pages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserTable;
